import { D20 } from '../../common/die';
import i18next from 'i18next';
import { makeKey } from '../../common/translationKey';

export enum NpcType {
  Minor,
  Notable,
  Major,
}

export class NpcTypeModel {
  type: NpcType;
  name: string;

  constructor(type: NpcType, name: string) {
    this.type = type;
    this.name = name;
  }

  get localizedName() {
    return i18next.t(makeKey('NpcType.', NpcType[this.type], '.name'));
  }

  get localizedDescription() {
    return i18next.t(makeKey('NpcType.', NpcType[this.type], '.description'));
  }
}

export class NpcTypes {
  static getNpcTypes() {
    return [
      new NpcTypeModel(NpcType.Minor, 'Minor NPC'),
      new NpcTypeModel(NpcType.Notable, 'Notable NPC'),
      new NpcTypeModel(NpcType.Major, 'Major NPC'),
    ];
  }

  static attributePoints(type: NpcType, simple: boolean = false) {
    if (type === NpcType.Minor) {
      return [9, 9, 8, 8, 7, 7];
    } else if (type === NpcType.Notable) {
      return [10, 9, 9, 8, 8, 7];
    } else if (simple) {
      return [10, 10, 9, 9, 9, 9];
    } else {
      const result = [7, 7, 7, 7, 7, 7];
      for (let i = 0; i < 14; i++) {
        let done = false;
        while (!done) {
          const attr = Math.floor(Math.random() * 6);
          if (result[attr] < 12) {
            result[attr] = result[attr] + 1;
            done = true;
          }
        }
      }
      result.sort().reverse();
      return result;
    }
  }

  static departmentPointCount(type: NpcType) {
    let result = 0;
    this.departmentPoints(type).forEach((n) => (result += n));
    return result;
  }

  static attributePointCount(type: NpcType) {
    let result = 0;
    this.attributePoints(type).forEach((n) => (result += n - 7));
    return result;
  }

  static departmentPoints(type: NpcType, simple: boolean = false) {
    if (type === NpcType.Minor) {
      return [2, 2, 1, 1, 0, 0];
    } else if (type === NpcType.Notable) {
      return [3, 2, 2, 1, 1, 0];
    } else if (simple) {
      return [3, 3, 3, 3, 2, 2];
    } else {
      const result = [1, 1, 1, 1, 1, 1];
      for (let i = 0; i < 10; i++) {
        let done = false;
        while (!done) {
          const disc = Math.floor(Math.random() * 6);
          if (result[disc] < 5) {
            result[disc] = result[disc] + 1;
            done = true;
          }
        }
      }
      result.sort().reverse();
      return result;
    }
  }

  static numberOfFocuses(type: NpcType) {
    if (type === NpcType.Minor) {
      return 0;
    } else if (type === NpcType.Notable) {
      return D20.roll() >= 10 ? 2 : 3;
    } else {
      const distribution = [
        3, 3, 4, 4, 4, 4, 5, 5, 5, 5, 5, 5, 5, 6, 6, 6, 6, 6, 6, 6,
      ];
      return distribution[D20.roll() - 1];
    }
  }

  static numberOfValues(type: NpcType) {
    if (type === NpcType.Minor) {
      return 0;
    } else if (type === NpcType.Notable) {
      return 1;
    } else {
      return 4;
    }
  }

  static numberOfTalents(type: NpcType) {
    if (type === NpcType.Minor) {
      return D20.roll() > 10 ? 1 : 2;
    } else if (type === NpcType.Notable) {
      return D20.roll() > 10 ? 2 : 3;
    } else {
      return 4;
    }
  }

  static getNpcTypeByName(name: string) {
    const all = [
      NpcType[NpcType.Minor],
      NpcType[NpcType.Notable],
      NpcType[NpcType.Major],
    ];
    return all.includes(name) ? (all.indexOf(name) as NpcType) : undefined;
  }
}
